import { useMemo } from "react"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { usePanelId } from "../context/panel-id"
import { DEFAULT_CORE_STATE } from "../store/core-slice"
import { DEFAULT_TRADE_STATE } from "../store/trade-slice"
import { useCoreActions } from "./use-core-actions"
import { useTradeActions } from "./use-trade-actions"

// Shared base for the panel data hooks: the identical core/model/trade reads + actions, grouped by
// domain. A data hook spreads `...core, ...trade` into its flat return, then layers its own state.
// Both bundles are memoized so their references stay stable and the caller's return memo holds.
export function useDashboardSliceData(fallbackPanelKey: string) {
  const dashboardId = useDashboardId()
  const panelId = usePanelId()
  const panelKey = panelId ?? fallbackPanelKey
  const dispatch = useAppDispatch()

  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const tradeState = useAppSelector((s) => s.dashboard.trade[dashboardId] ?? DEFAULT_TRADE_STATE)

  const coreActions = useCoreActions()
  const tradeActions = useTradeActions()

  const core = useMemo(() => ({ ...coreState, ...coreActions }), [coreState, coreActions])

  // Only the trade fields panels read — not the whole slice (e.g. setExpanded ownership stays on trade).
  const trade = useMemo(() => ({
    trades: tradeState.trades,
    isExpanded: tradeState.isExpanded,
    tradedPosition: tradeState.tradedPosition,
    tradedPositionField: tradeState.tradedPositionField,
    actionsBase: tradeState.actionsBase,
    assetViewGroupBy: tradeState.assetViewGroupBy,
    ...tradeActions,
  }), [tradeState, tradeActions])

  return { dashboardId, panelKey, dispatch, modelId, core, trade }
}
